import { PagesPermissions } from '@/models/Permissions/enum/PagesPermissions';
import { selectThemeMode } from '@/store/app/selectors';
import { ThemeModes } from '@/themes/enums';
import { useAppSelector, useUserHasPermissions } from '@store/hooks';
import { isDef } from '@utils/def';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ICONS_TYPES } from '../Icon';

import SubMenu from './components/SubMenu';
import { ButtonItems } from './constants';
import { NavItemId } from './enums';
import {
  ButtonContainer,
  CollapsedHexIcon,
  CollapsedLogoButton,
  Divider,
  DrawerHeader,
  ExpandHintIcon,
  FooterContainer,
  HomeLinkContainer,
  LogoIcon,
  OpenpanelIcon,
  Root,
  StyledDrawer,
} from './styles';
import { Props } from './types';

const LeftNavbar = ({ className, isSidebarOpen, setIsSidebarOpen }: Props) => {
  const { isAuthenticated, permissions } = useAppSelector(
    (state) => state.auth,
  );
  const themeMode = useSelector(selectThemeMode);
  const navigate = useNavigate();

  const [showSubNav, setShowSubNav] = useState<string[]>([]);

  const hasMarketDataWidgets = useUserHasPermissions(
    PagesPermissions.marketDataWidgetsPage,
  );

  const filteredNavButtonsByPermissions = useMemo(() => {
    const filtered = ButtonItems.map((item) => {
      if (item.subNav) {
        const filteredSubItems = item.subNav.filter(
          (subItem) =>
            !isDef(subItem.permission) ||
            permissions.includes(subItem.permission),
        );

        if (
          item.path === '/fund-level-center' &&
          filteredSubItems.length === 1
        ) {
          return {
            ...item,
            subNav: undefined,
            path: filteredSubItems[0].path,
          };
        }

        return { ...item, subNav: filteredSubItems };
      }

      return item;
    }).filter((item) => {
      if (isDef(item.subNav) && !item.subNav.length) {
        return false;
      }

      // Only show Market Data Curves if user has marketDataCurvesPage but NOT marketDataWidgetsPage
      if (
        item.permission === PagesPermissions.marketDataCurvesPage &&
        hasMarketDataWidgets
      ) {
        return false;
      }

      return !isDef(item.permission) || permissions.includes(item.permission);
    });

    // Remove section labels that have no items underneath them
    return filtered.filter((item, index) => {
      if (item.type !== 'section') return true; // always keep non-section items

      // Check if the next item exists and is not another section label
      const nextItem = filtered[index + 1];
      return isDef(nextItem) && nextItem.type !== 'section';
    });
  }, [permissions, hasMarketDataWidgets]);

  const sideMenuToggleHandler = () => {
    if (isSidebarOpen) {
      setShowSubNav([]);
    }
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSettingsIconClick = () => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
      setShowSubNav((prev) =>
        prev.includes(NavItemId.SETTINGS)
          ? prev
          : [...prev, NavItemId.SETTINGS],
      );
    }
  };
  return (
    <Root className={className}>
      <StyledDrawer
        variant="permanent"
        open={isSidebarOpen}
        $isSideBarOpen={isSidebarOpen}>
        <DrawerHeader $isSidebarOpen={isSidebarOpen}>
          {isSidebarOpen ? (
            <>
              <HomeLinkContainer onClick={() => navigate('/home')}>
                <LogoIcon
                  icon={
                    themeMode === ThemeModes.light
                      ? ICONS_TYPES.LogoDarkBlue
                      : ICONS_TYPES.LogoOrange
                  }
                  size={100}
                />
              </HomeLinkContainer>
              <OpenpanelIcon
                onClick={sideMenuToggleHandler}
                icon={ICONS_TYPES.SidepanelCollapse}
                size={30}
              />
            </>
          ) : (
            <CollapsedLogoButton onClick={sideMenuToggleHandler}>
              <CollapsedHexIcon
                icon={
                  themeMode === ThemeModes.light
                    ? ICONS_TYPES.LogoShortDarkBlue
                    : ICONS_TYPES.LogoShortOrange
                }
                size={32}
              />
              <ExpandHintIcon
                icon={
                  themeMode === ThemeModes.light
                    ? ICONS_TYPES.Sidepanel
                    : ICONS_TYPES.SidepanelOrange
                }
                size={24}
              />
            </CollapsedLogoButton>
          )}
        </DrawerHeader>
        <Divider />
        {isAuthenticated && (
          <>
            <ButtonContainer $isOpen={isSidebarOpen}>
              {filteredNavButtonsByPermissions
                .filter((item) => item.title !== NavItemId?.SETTINGS)
                .map((item, index) => (
                  <SubMenu
                    item={item}
                    isOpenedNavBar={isSidebarOpen}
                    key={index}
                    showSubNav={showSubNav}
                    setShowSubNav={setShowSubNav}
                  />
                ))}
            </ButtonContainer>

            <FooterContainer>
              {filteredNavButtonsByPermissions.find(
                (item) => item.title === NavItemId.SETTINGS,
              ) && (
                <SubMenu
                  item={
                    filteredNavButtonsByPermissions.find(
                      (item) => item.title === NavItemId.SETTINGS,
                    )!
                  }
                  isOpenedNavBar={isSidebarOpen}
                  showSubNav={showSubNav}
                  setShowSubNav={setShowSubNav}
                  onCollapsedClick={handleSettingsIconClick}
                />
              )}
            </FooterContainer>
          </>
        )}
      </StyledDrawer>
    </Root>
  );
};

export default LeftNavbar;
